import type MailboxAutoReplyConfigurationOptions from 'owa-service/lib/contract/MailboxAutoReplyConfigurationOptions';
import type OofState from 'owa-service/lib/contract/OofState';
import type { OwaDate } from 'owa-datetime';
import { isBefore, isWithinRange, now, userDate } from 'owa-datetime';

/**
 * Util to determine if Automatic replies is enabled or not
 * @param autoReplyConfigOptions automatic replies options
 */
export default function isAutomaticRepliesEnabledUtil(
    autoReplyConfigOptions: MailboxAutoReplyConfigurationOptions
): boolean {
    return (
        !!autoReplyConfigOptions &&
        isAutomaticRepliesEnabled(autoReplyConfigOptions.AutoReplyState) &&
        isCurrentTimeWithinAutomaticRepliesTimeRange(
            autoReplyConfigOptions.StartTime,
            autoReplyConfigOptions.EndTime
        )
    );
}

/**
 * This function returns true if user turned on the automatic replies option.
 *  @param autoReplyState automatic replies state
 */
const isAutomaticRepliesEnabled = (autoReplyState?: OofState): boolean =>
    autoReplyState === 1 || autoReplyState === 2;

/**
 * This function tells if the "Automatic Reply" banner needs to be shown or not.
 * For scheduled automatic replies, we should not show banner to turn OFF "Automatic Reply",
 * as the user intentionally has turned that ON for the specified period.
 * @param autoReplyConfigOptions automatic reply configurations
 * @return [boolean]
 */
export const shouldShowAutomaticRepliesBanner = (
    autoReplyConfigOptions: MailboxAutoReplyConfigurationOptions
): boolean =>
    isAutomaticRepliesEnabledUtil(autoReplyConfigOptions) &&
    !isAutomaticRepliesScheduled(autoReplyConfigOptions?.AutoReplyState);

/**
 * This function tells if user has set the start and end time for automatic replies.
 * @param autoReplyState automatic replies state
 * @return [boolean]
 */
const isAutomaticRepliesScheduled = (autoReplyState?: OofState): boolean => autoReplyState === 2;

/**
 * This function returns true if current time is within the time range of automatic replies.
 *  @param startTime automatic replies start time as iso-string
 *  @param endTime automatic replies end time as iso-string
 */
const isCurrentTimeWithinAutomaticRepliesTimeRange = (
    startTime?: string,
    endTime?: string
): boolean => {
    if (!startTime || !endTime) {
        return false;
    }
    const currentTime: OwaDate = now();
    const autoReplyStartTime: OwaDate = userDate(startTime);
    const autoReplyEndTime: OwaDate = userDate(endTime);
    return (
        !isBefore(autoReplyEndTime, autoReplyStartTime) &&
        isWithinRange(currentTime, autoReplyStartTime, autoReplyEndTime)
    );
};
